

import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { filesize } from "filesize";


import ApiButton from "@/vue/components/ApiButton.vue";
import ContentTypes from "@/utilities/ContentTypes";
import ApiClient from "@/utilities/ApiClient";
import imageTools from "@/utilities/ImageTools";
import FileDownload from "@/utilities/FileDownload";
import { IFileUploadResponse } from "@/model/IFileUploadResponse";
import * as toastr from "toastr";
import Utils from "@/utilities/Utils";

@Component({
    components: { ApiButton }
})
export default class FileUploadDropZone extends Vue {

        // chosenFile: File|null = null;

        // isUploading: boolean = false;

        // @Prop({ required: true }) resource!: Resource;
        // @Prop({ required: true }) label!: string;
    @Prop({ default: "" }) accept!: string;
    @Prop({ default: "drop a file here" }) dropZoneLabel!: string;
    @Prop({ default: "choose a file" }) browseLabel!: string;
    @Prop({ default: 0 }) maxFileSize!: number;
    @Prop({ default: false }) isMultiple!: boolean;
    @Prop({ default: false }) isPublicUpload!: boolean; // public has progressive delay to help with DoS attacks


        // @Prop({ default: false }) capture!: string|boolean;
        // @Prop({ default: () => [] }) rules!: Array<(() => string | boolean) | ((v: any) => string | true)>;
        // @Prop({ default: false }) isPublicUpload!: boolean; // public has progressive delay to help with DoS attacks

    mounted(): void {
        this.setUpDragDrop();
    }

    //const fileDropZone = this.$refs.fileDropZone as HTMLElement;

    async onFileInputChanged(event: Event) {
        await this.upload(event);
    }

    setUpDragDrop() {
        const dropZoneElement = this.$refs.fileDropZone as HTMLElement;

        if (!dropZoneElement) {
            console.warn("setUpDragDrop - fileDropZone element is missing");
            toastr.error("Failed to initialise drag-drop");
            return;
        }

        // prevent defaults
        ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
            dropZoneElement.addEventListener(eventName, event => {
                event.preventDefault();
                event.stopPropagation();
            }, false);
        });

        // our own events
        dropZoneElement.addEventListener("dragover", () => {
            dropZoneElement.className = "drop-zone-drag-over";
            return false;
        }, false);

        dropZoneElement.addEventListener("dragleave", () => {
            dropZoneElement.className = "drop-zone";
            return false;
        }, false);

        dropZoneElement.addEventListener("dragend", () => {
            dropZoneElement.className = "drop-zone";
            return false;
        }, false);

        dropZoneElement.addEventListener("drop", async (event: DragEvent) => {
            const info = this.getDroppedFileInfo(event);
            if (!this.isMultiple && info.count !== 1) {
                toastr.warning("You need to drag a single file"); // emit instead of toast?
                dropZoneElement.className = "drop-zone";
                return false;
            }

            // TODO - how does this work for multiple?
            if (this.accept) {
                const isOk = this.accept.indexOf("/*")
                    ? info.type.indexOf(this.accept.replace("/*", "/")) > -1
                    : info.type === this.accept;
                //console.log(`ok=${isOk} | this.accept=${this.accept} | info.type=${info.type}`);
                if (!isOk) {
                    // TODO - emit
                    toastr.warning("File needs to be of type ." + ContentTypes.extension(this.accept).toUpperCase());
                    dropZoneElement.className = "drop-zone";
                    return false;
                }
            }

            await this.upload(event);

            dropZoneElement.className = "drop-zone";
        }, false);
    }

    //
    // -- helper functions....
    //

    // don't show drag-drop message on narrow displays (mobile devices)
    isNarrowScreen(threshold?: number): boolean {
        return window.innerWidth < (threshold ?? 600);
    }

    // from either drag-drop OR file input
    async upload(event: DragEvent | Event): Promise<void> {

        let files: Array<File> = (event as any).dataTransfer
            ? ((event as DragEvent).dataTransfer)?.files
            : ((event as any).target)?.files;

        if (typeof files === "undefined" || files === null || files.length === 0) {
            toastr.error("Files missing.", "File upload error");
            return Promise.reject(null);
        }

        if(!this.isMultiple) {
            // only the first file if multiple
            files = [files[0]];
        }

        // raise event of files added / queued

        this.$emit("files-queued", Array.from(files).map(f => f.name));

        // fire all at once with promise all?
        for(const file of files) {
            const uploadResponse = await this.checkResizeThenPost(file);
            this.$emit("file-uploaded", uploadResponse);
        }
    }

    async checkResizeThenPost (file: File): Promise<IFileUploadResponse> {

        const isImage = await imageTools.testToSeeIfImage(file);
        if (isImage) {
            // width + height are *maximum*
            const maxSize = { width: 1000, height: 1000 };
            const result = await imageTools.resize(file, maxSize)
            console.log("resized:", result.didResize);

            if (result.didResize && result.file) {
                return await this.postFile(file, result.file as Blob);
            } else {
                return await this.postFile(file);
            }
        }
        else {
            return await this.postFile(file);
        }
    }

    async postFile(file: File, imageBlob?: Blob): Promise<IFileUploadResponse> {
    //async postFile(file: File, imageBlob?: Blob): Promise<void> {

        const size = imageBlob && imageBlob.size ? imageBlob.size : file.size;

        if(this.maxFileSize > 0 && size > this.maxFileSize) {
            return {
                extension: "",
                filename: "",
                httpContentType: "",
                originalFilename: file.name,
                size,
                error: `"File exceeds maximum size of ${filesize(this.maxFileSize, { base: 2 })}`
            };
        }

        const formData = new FormData();
        formData.append("formFile", file);
        //formData.append("someID", "666");
        //this.isUploading = true;
        try {
            const path = this.isPublicUpload ? "/api/file/uploadPublic" : "/api/file/upload";
            return await ApiClient.postNonJson(path, formData) as IFileUploadResponse;
        } catch (error: any) {
            return {
                extension: "",
                filename: "",
                httpContentType: "",
                originalFilename: file.name,
                size,
                error: error.toString()
            };
        }
    }

    getDroppedFileInfo(event: DragEvent) {
        const info = { count: 0, type: "" };
        info.count = event.dataTransfer?.items.length ?? 0;
        if (info.count !== 1) return info;

        if (event.dataTransfer?.items) {
            // Use DataTransferItemList interface to access the file(s)
            if (event.dataTransfer.items[0].kind !== "file") {
                // not a file.  reject.
                info.count = 0;
                return info;
            }
            const file = event.dataTransfer.items[0].getAsFile();
            info.type = file?.type ?? "";
        }
        else {
            // Use DataTransfer interface to access the file(s)
            info.type = event.dataTransfer?.files[0].type ?? "";
        }
        return info;
    }






// -- old code

        // // get iconUrl(): string {
        // //     if(!this.resource.hasFile) return ApiClient.resolveUrl("api/file/icon?extension=nul");
        // //     const filename = this.resource.filename;
        // //     const dotAt = filename.lastIndexOf(".");
        // //     const extension = dotAt === -1 || dotAt >= filename.length - 1 ? "" : filename.substr(dotAt + 1).toLowerCase();
        // //     return ApiClient.resolveUrl(`api/file/icon?extension=${extension}`);
        // // }

        // async onChange(file: File|null): Promise<void> {
        //     this.chosenFile = file;
        //     console.log("onInput ", file);
        //     if(file == null) return;
        //     //const fileInput: any = this.$refs.fileInput;
        //     //fileInput.loading = true;
        //     await this.uploadFile(file);
        // }

        // async onFileDropped(event: any): Promise<void> {
        //     const file = event?.dataTransfer?.files[0]
        //     if(!file) return;
        //     await this.uploadFile(file);
        // }

        // async uploadFile(file: File): Promise<void> {
        //     const formData = new FormData();
        //     formData.append("formFile", file);
        //     //formData.append("someID", "666");
        //     this.isUploading = true;
        //     try {
        //         const path = this.isPublicUpload ? "/api/file/uploadPublic" : "/api/file/upload";
        //         const response: IFileUploadResponse = await ApiClient.postNonJson(path, formData);
        //         this.resource.httpContentType = response.httpContentType;
        //         this.resource.originalFilename = response.originalFilename;
        //         this.resource.filename = response.filename;
        //         this.resource.size = response.size;
        //         this.isUploading = false;
        //         this.$emit("uploaded", this.resource);
        //     } catch (error) {
        //         this.isUploading = false;
        //         toastr.error("Upload failed");
        //     }
        // }

        // @Watch("chosenFile", { immediate: false, deep: true })
        // async onChosenFilechanged(): Promise<void> {
        //     console.log("onChosenFileChanged ", this.chosenFile);
        // }

        // async clear(): Promise<void> {
        //     this.chosenFile = null;
        //     Utils.resetObject(this.resource);
        // }

        // async download(): Promise<void> {
        //     FileDownload.download(this.resource);
        // }
}
